import { createContext, useContext, useEffect, useMemo, useState } from 'react';

type Theme = 'dark' | 'light' | 'system';

type ThemeProviderProps = {
	children: React.ReactNode;
	defaultTheme?: Theme;
	storageKey?: string;
};

type ThemeProviderState = {
	theme: Theme;
	setTheme: (theme: Theme) => void;
	getDarkOrLight: () => Exclude<Theme, 'system'>;
};

const ThemeProviderContext = createContext<ThemeProviderState | undefined>(
	undefined,
);

const isBrowser = () => typeof window !== 'undefined';

const getPrefersColorScheme = (): 'dark' | 'light' => {
	if (!isBrowser() || !window.matchMedia) return 'light';
	return window.matchMedia('(prefers-color-scheme: dark)').matches
		? 'dark'
		: 'light';
};

export function ThemeProvider({
	children,
	defaultTheme = 'system',
	storageKey = 'vite-ui-theme',
}: ThemeProviderProps) {
	// Don’t read localStorage here — this runs on server too.
	const [theme, setThemeState] = useState<Theme>(defaultTheme);

	// On mount, hydrate from localStorage (client only)
	useEffect(() => {
		if (!isBrowser()) return;
		try {
			const fromStorage = localStorage.getItem(storageKey) as Theme | null;
			if (fromStorage) setThemeState(fromStorage);
		} catch {
			// ignore storage errors (Safari private mode, etc.)
		}
	}, [storageKey]);

	// Apply class + persist whenever theme changes (client only)
	useEffect(() => {
		if (!isBrowser()) return;

		const root = window.document.documentElement;
		root.classList.remove('light', 'dark');

		const resolved = theme === 'system' ? getPrefersColorScheme() : theme;
		root.classList.add(resolved);

		try {
			localStorage.setItem(storageKey, theme);
		} catch {
			// ignore storage errors
		}
	}, [theme, storageKey]);

	// If using system, update on OS theme change
	useEffect(() => {
		if (!isBrowser() || theme !== 'system' || !window.matchMedia) return;
		const mql = window.matchMedia('(prefers-color-scheme: dark)');
		const handler = () => {
			const root = window.document.documentElement;
			root.classList.remove('light', 'dark');
			root.classList.add(getPrefersColorScheme());
		};
		mql.addEventListener?.('change', handler);
		return () => mql.removeEventListener?.('change', handler);
	}, [theme]);

	// (Optional) sync between tabs
	useEffect(() => {
		if (!isBrowser()) return;
		const onStorage = (e: StorageEvent) => {
			if (e.key === storageKey && e.newValue) {
				setThemeState(e.newValue as Theme);
			}
		};
		window.addEventListener('storage', onStorage);
		return () => window.removeEventListener('storage', onStorage);
	}, [storageKey]);

	const value = useMemo<ThemeProviderState>(
		() => ({
			theme,
			setTheme: setThemeState,
			getDarkOrLight: () =>
				theme === 'system' ? getPrefersColorScheme() : theme,
		}),
		[theme],
	);

	return (
		<ThemeProviderContext.Provider value={value}>
			{children}
		</ThemeProviderContext.Provider>
	);
}

export const useTheme = () => {
	const ctx = useContext(ThemeProviderContext);
	if (!ctx) throw new Error('useTheme must be used within a ThemeProvider');
	return ctx;
};
